import React, { useState, useEffect } from 'react';
import { ConsultationTier, ConsultationTierId, ConsultationPetition } from './types';
import { useAudio } from '../audio/useAudio';

export const CONSULTATION_TIERS: ConsultationTier[] = [
  {
    id: 'tier1',
    name: 'Tier I: Forensic Demurrer',
    duration: '60-Minute Direct Consultation',
    description:
      'Rigorous juridical boundary audit. Dissolution and formal demurrer of false spiritual debts, inherited orthodox oaths, and astral liens.'
  },
  {
    id: 'tier2',
    name: 'Tier II: Altar Architecture & Consecration',
    duration: 'Multi-Phase Operative Guidance',
    description:
      'Consecration of the Northern Bedrock Altar. Dukante elemental current grounding, basalt & iron correspondences, and nocturnal rite architecture.'
  },
  {
    id: 'tier3',
    name: 'Tier III: Formal Dedication to King Belial',
    duration: '30-Day Guided Sponsorship',
    description:
      'Full covenant dedication under the Law of the Soil. Step-by-step guidance through the Enn, offerings, and permanent severance of all rival yokes.'
  },
  {
    id: 'tier4',
    name: 'Tier IV: The 80 Legions Conclave Entry',
    duration: 'Inner Curia Vetting',
    description:
      'Vetting petition for admittance into the closed Discord Curia Chalicitis and high-ranking demonic juridical councils.'
  }
];

export const ORIGIN_CURRENTS = [
  'Luciferian Gnosticism / Left-Hand Path',
  'Traditional Demonolatry (Dukante / Goetic)',
  'Qliphothic & Sitra Achra Working',
  'Solitary Thelemite / Chaos Magician',
  'Uninitiated Seeker of Radical Sovereignty'
];

export const CONSULTATION_STORAGE_KEY = 'belial_consultation_history';

/**
 * Generates a tamper-evident cryptographic seal in format:
 * BELIAL-[YEAR]-[HASH4]-[HEX4]
 */
export function generatePetitionSeal(moniker: string, statement: string, timestamp: number): string {
  const year = new Date(timestamp).getFullYear();
  let hash = 0;
  const combined = `${moniker}:${statement}:${timestamp}`;
  for (let i = 0; i < combined.length; i++) {
    hash = (hash << 5) - hash + combined.charCodeAt(i);
    hash |= 0;
  }
  const hashHex = (Math.abs(hash) % 0xffff).toString(16).toUpperCase().padStart(4, '0');
  const randomHex = Math.floor(Math.random() * 0xffff).toString(16).toUpperCase().padStart(4, '0');
  return `BELIAL-${year}-${hashHex}-${randomHex}`;
}

export const ConsultationPortal: React.FC = () => {
  const { playAnvilStrike } = useAudio();

  // Form State
  const [moniker, setMoniker] = useState<string>('');
  const [vessel, setVessel] = useState<string>('');
  const [currentOfOrigin, setCurrentOfOrigin] = useState<string>(ORIGIN_CURRENTS[0]);
  const [tierId, setTierId] = useState<ConsultationTierId>('tier1');
  const [statement, setStatement] = useState<string>('');
  const [affirmed, setAffirmed] = useState<boolean>(false);

  // UI / Status State
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeReceipt, setActiveReceipt] = useState<ConsultationPetition | null>(null);
  const [pastPetitions, setPastPetitions] = useState<ConsultationPetition[]>([]);
  const [copiedSeal, setCopiedSeal] = useState<boolean>(false);
  const [showHistory, setShowHistory] = useState<boolean>(false);

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(CONSULTATION_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setPastPetitions(parsed);
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!moniker.trim()) {
      newErrors.moniker = 'A spiritual moniker or true name is required.';
    }

    const trimmedVessel = vessel.trim();
    if (!trimmedVessel) {
      newErrors.vessel = 'A communication vessel (email or Discord handle) is required.';
    } else {
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedVessel);
      const isDiscord = /^@?[\w.-]{2,32}(#\d{4})?$/.test(trimmedVessel);
      if (!isEmail && !isDiscord) {
        newErrors.vessel = 'Enter a valid email (e.g. adept@void.org) or Discord handle (e.g. @vindex or vindex#0000).';
      }
    }

    if (statement.trim().length < 20) {
      newErrors.statement = `Statement of intent must be at least 20 characters (current: ${statement.trim().length}).`;
    }

    if (!affirmed) {
      newErrors.affirmed = 'You must affirm radical sovereignty before the Council of Earth.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    // Trigger ritual impact audio
    try {
      playAnvilStrike();
    } catch {
      // Audio fallback
    }

    const now = Date.now();
    const sealId = generatePetitionSeal(moniker, statement, now);

    const petition: ConsultationPetition = {
      id: sealId,
      moniker: moniker.trim(),
      vessel: vessel.trim(),
      currentOfOrigin,
      tierId,
      statement: statement.trim(),
      affirmed: true,
      timestamp: new Date(now).toISOString()
    };

    const updatedHistory = [petition, ...pastPetitions];
    setPastPetitions(updatedHistory);
    setActiveReceipt(petition);

    try {
      localStorage.setItem(CONSULTATION_STORAGE_KEY, JSON.stringify(updatedHistory));
    } catch {
      // Ignore localStorage write errors
    }
  };

  const handleCopySeal = async (seal: string) => {
    try {
      await navigator.clipboard.writeText(seal);
      setCopiedSeal(true);
      setTimeout(() => setCopiedSeal(false), 2000);
    } catch {
      // Clipboard write fallback
    }
  };

  const handleResetForm = () => {
    setActiveReceipt(null);
    setMoniker('');
    setVessel('');
    setStatement('');
    setAffirmed(false);
    setErrors({});
  };

  return (
    <div className="chthonic-card rounded-lg p-6 sm:p-8 space-y-8" id="consultation-portal">
      {/* Portal Header */}
      <div className="border-b border-iron-dark pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <span className="text-[10px] font-mono text-gold-ancient uppercase tracking-widest block">
            TRIBUNAL CONSILII • INITIATORY INTAKE
          </span>
          <h3 className="font-display text-xl sm:text-2xl text-parchment-light mt-1">
            Initiatory Consultation & Council Petition
          </h3>
        </div>

        {pastPetitions.length > 0 && (
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="text-xs font-mono text-gold-bright border border-iron-base bg-obsidian-surface px-3 py-1.5 rounded hover:border-gold-bright transition-colors self-start sm:self-auto"
          >
            {showHistory ? 'Hide Past Seals' : `View Seals (${pastPetitions.length})`}
          </button>
        )}
      </div>

      {/* Past Seals History Drawer */}
      {showHistory && pastPetitions.length > 0 && (
        <div className="rounded-lg border border-iron-dark bg-obsidian-deep p-4 sm:p-5 space-y-3 animate-fade-in">
          <div className="flex items-center justify-between border-b border-iron-dark pb-2">
            <span className="text-xs font-display text-gold-ancient uppercase tracking-wider">
              Recorded Petitions in Bedrock Memory
            </span>
            <span className="text-[11px] font-mono text-iron-steel">
              {pastPetitions.length} Recorded
            </span>
          </div>

          <div className="space-y-2 max-h-56 overflow-y-auto pr-1 text-xs">
            {pastPetitions.map((p) => (
              <div
                key={p.id}
                className="rounded border border-iron-dark/60 bg-obsidian-surface/60 p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
              >
                <div>
                  <span className="font-mono text-gold-bright font-bold block">{p.id}</span>
                  <span className="text-parchment-dim text-[11px]">
                    {p.moniker} • {p.currentOfOrigin} • {new Date(p.timestamp).toLocaleDateString()}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopySeal(p.id)}
                  className="rounded border border-iron-base bg-obsidian-deep px-3 py-1 text-[11px] font-mono text-parchment-dim hover:text-gold-bright hover:border-gold-bright self-start sm:self-auto"
                >
                  Copy Seal
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* RECEIPT VIEW */}
      {activeReceipt ? (
        <div className="rounded-lg border-2 border-gold-ancient bg-obsidian-deep/95 p-6 sm:p-8 space-y-6 shadow-glow-gold/30 animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-iron-dark pb-4">
            <div className="space-y-1">
              <span className="text-[10px] font-mono text-emerald-400 uppercase tracking-widest flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                PETITION SEALED & SUBMITTED TO CONCLAVE
              </span>
              <h4 className="font-display text-xl sm:text-2xl text-gold-bright font-bold">
                Verification Seal Issued
              </h4>
            </div>

            <div className="text-right">
              <span className="text-[10px] font-mono text-iron-steel uppercase block">
                SEAL IDENTIFIER
              </span>
              <span className="font-mono text-sm sm:text-base text-gold-radiant font-bold tracking-wider">
                {activeReceipt.id}
              </span>
            </div>
          </div>

          {/* Receipt Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-serif">
            <div className="rounded border border-iron-dark/80 bg-obsidian-surface p-4 space-y-2">
              <div className="flex justify-between border-b border-iron-dark/40 pb-1.5">
                <span className="text-iron-steel uppercase font-mono text-[10px]">MONIKER:</span>
                <span className="text-parchment-light font-display">{activeReceipt.moniker}</span>
              </div>
              <div className="flex justify-between border-b border-iron-dark/40 pb-1.5">
                <span className="text-iron-steel uppercase font-mono text-[10px]">VESSEL:</span>
                <span className="text-parchment-light font-mono">{activeReceipt.vessel}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-iron-steel uppercase font-mono text-[10px]">CURRENT:</span>
                <span className="text-parchment-dim text-right max-w-[65%]">{activeReceipt.currentOfOrigin}</span>
              </div>
            </div>

            <div className="rounded border border-iron-dark/80 bg-obsidian-surface p-4 space-y-2">
              <div className="flex justify-between border-b border-iron-dark/40 pb-1.5">
                <span className="text-iron-steel uppercase font-mono text-[10px]">TIER:</span>
                <span className="text-gold-bright font-display">
                  {CONSULTATION_TIERS.find(t => t.id === activeReceipt.tierId)?.name || activeReceipt.tierId}
                </span>
              </div>
              <div className="flex justify-between border-b border-iron-dark/40 pb-1.5">
                <span className="text-iron-steel uppercase font-mono text-[10px]">STATUS:</span>
                <span className="text-emerald-400 font-mono text-[11px]">CONSECRATED IN BEDROCK</span>
              </div>
              <div className="flex justify-between">
                <span className="text-iron-steel uppercase font-mono text-[10px]">TIMESTAMP:</span>
                <span className="text-parchment-dim font-mono text-[10px]">
                  {new Date(activeReceipt.timestamp).toUTCString()}
                </span>
              </div>
            </div>
          </div>

          {/* Statement Excerpt */}
          <div className="rounded border border-iron-dark bg-obsidian-base p-4 space-y-2">
            <span className="text-[10px] font-mono text-gold-ancient uppercase tracking-wider block">
              TESTAMENT OF INTENT & REVOLT:
            </span>
            <p className="text-xs text-parchment-base italic font-serif leading-relaxed">
              "{activeReceipt.statement}"
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={() => handleCopySeal(activeReceipt.id)}
              className="btn-gold flex-1 py-3 text-xs font-display tracking-widest uppercase rounded shadow-glow-bronze text-center"
            >
              {copiedSeal ? '✓ SEAL COPIED TO CLIPBOARD' : 'COPY CRYPTOGRAPHIC SEAL'}
            </button>

            <button
              type="button"
              onClick={handleResetForm}
              className="rounded border border-iron-base bg-obsidian-surface px-6 py-3 text-xs font-mono text-parchment-dim hover:text-gold-bright hover:border-gold-bright transition-colors"
            >
              Submit Another Petition
            </button>
          </div>
        </div>
      ) : (
        /* INTAKE FORM */
        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          {/* Identity & Vessel Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="moniker" className="block text-xs font-display tracking-wider text-parchment-light uppercase">
                Spiritual Moniker or Magical Motto *
              </label>
              <input
                id="moniker"
                name="moniker"
                type="text"
                value={moniker}
                onChange={e => setMoniker(e.target.value)}
                placeholder="e.g. Frater Vindex, Soror Chthonia"
                className={`w-full rounded border bg-obsidian-deep px-4 py-3 text-xs sm:text-sm text-parchment-light placeholder-iron-muted focus:outline-none transition-colors ${
                  errors.moniker ? 'border-ember-flare ring-1 ring-ember-flare' : 'border-iron-dark focus:border-gold-bright'
                }`}
              />
              {errors.moniker && (
                <p className="text-[11px] font-mono text-ember-flare">{errors.moniker}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="vessel" className="block text-xs font-display tracking-wider text-parchment-light uppercase">
                Vessel of Communication (Email or Discord Handle) *
              </label>
              <input
                id="vessel"
                name="vessel"
                type="text"
                value={vessel}
                onChange={e => setVessel(e.target.value)}
                placeholder="e.g. adept@sanctum.org or @vindex"
                className={`w-full rounded border bg-obsidian-deep px-4 py-3 text-xs sm:text-sm text-parchment-light placeholder-iron-muted focus:outline-none transition-colors ${
                  errors.vessel ? 'border-ember-flare ring-1 ring-ember-flare' : 'border-iron-dark focus:border-gold-bright'
                }`}
              />
              {errors.vessel && (
                <p className="text-[11px] font-mono text-ember-flare">{errors.vessel}</p>
              )}
            </div>
          </div>

          {/* Current of Origin Dropdown */}
          <div className="space-y-2">
            <label htmlFor="currentOfOrigin" className="block text-xs font-display tracking-wider text-parchment-light uppercase">
              Current of Origin / Prior Practice *
            </label>
            <select
              id="currentOfOrigin"
              name="currentOfOrigin"
              value={currentOfOrigin}
              onChange={e => setCurrentOfOrigin(e.target.value)}
              className="w-full rounded border border-iron-dark bg-obsidian-deep px-4 py-3 text-xs sm:text-sm text-parchment-light focus:border-gold-bright focus:outline-none transition-colors"
            >
              {ORIGIN_CURRENTS.map(c => (
                <option key={c} value={c} className="bg-obsidian-deep text-parchment-light">
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Consultation Tier Selection */}
          <div className="space-y-3">
            <label className="block text-xs font-display tracking-wider text-parchment-light uppercase">
              Select Chamber of Consultation *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CONSULTATION_TIERS.map(t => {
                const isSelected = tierId === t.id;
                return (
                  <div
                    key={t.id}
                    onClick={() => setTierId(t.id)}
                    className={`cursor-pointer rounded-lg border p-4 sm:p-5 transition-all duration-200 ${
                      isSelected
                        ? 'border-gold-bright bg-obsidian-deep shadow-glow-gold/20'
                        : 'border-iron-dark bg-obsidian-surface/60 hover:border-iron-base'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h5 className={`font-display text-xs sm:text-sm font-bold ${
                        isSelected ? 'text-gold-bright' : 'text-parchment-light'
                      }`}>
                        {t.name}
                      </h5>
                      <input
                        type="radio"
                        name="tierSelection"
                        checked={isSelected}
                        onChange={() => setTierId(t.id)}
                        className="text-gold-bright focus:ring-gold-bright"
                      />
                    </div>
                    <span className="text-[10px] font-mono text-gold-ancient block mb-2">
                      {t.duration}
                    </span>
                    <p className="text-xs text-parchment-dim font-serif leading-relaxed">
                      {t.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Statement of Intent */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label htmlFor="statement" className="block text-xs font-display tracking-wider text-parchment-light uppercase">
                Statement of Intent & Personal Yokes to Sever *
              </label>
              <span className={`text-[10px] font-mono ${
                statement.trim().length >= 20 ? 'text-emerald-400' : 'text-iron-muted'
              }`}>
                {statement.trim().length} / 20 chars min
              </span>
            </div>
            <textarea
              id="statement"
              name="statement"
              rows={4}
              value={statement}
              onChange={e => setStatement(e.target.value)}
              placeholder="Detail the spiritual bonds, orthodox guilts, or celestial liens you seek to expunge, and your readiness to stand as an autonomous earthly sovereign before King Belial..."
              className={`w-full rounded border bg-obsidian-deep p-4 text-xs sm:text-sm text-parchment-light placeholder-iron-muted focus:outline-none transition-colors ${
                errors.statement ? 'border-ember-flare ring-1 ring-ember-flare' : 'border-iron-dark focus:border-gold-bright'
              }`}
            />
            {errors.statement && (
              <p className="text-[11px] font-mono text-ember-flare">{errors.statement}</p>
            )}
          </div>

          {/* Sovereign Affirmation Checkbox */}
          <div className="rounded-lg border border-iron-dark bg-obsidian-deep/80 p-4 space-y-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                id="affirmed"
                name="affirmed"
                type="checkbox"
                checked={affirmed}
                onChange={e => setAffirmed(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-iron-dark bg-obsidian-surface text-gold-bright focus:ring-gold-bright"
              />
              <span className="text-xs text-parchment-base font-serif leading-relaxed">
                <strong className="text-gold-bright font-display font-semibold">SOVEREIGN DECLARATION:</strong> I affirm of my own conscious, uncoerced will that I submit this petition beholden to no gods, no masters, and no unexamined moral liens. I accept full responsibility for my vessel and swear allegiance only to the sovereign bedrock of my own divine flame.
              </span>
            </label>
            {errors.affirmed && (
              <p className="text-[11px] font-mono text-ember-flare pl-7">{errors.affirmed}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn-gold w-full py-4 text-xs sm:text-sm font-display tracking-widest uppercase rounded shadow-glow-bronze flex items-center justify-center gap-2"
          >
            <span>Strike Anvil & Dispatch Petition to Council</span>
            <span>⚡</span>
          </button>
        </form>
      )}
    </div>
  );
};

export default ConsultationPortal;
